import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { numerologyTradingClient } from '../services/numerologyTradingClient';
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/Card';
import Button from './ui/Button';
import { theme } from '../styles/design-system';

const ProfessionalNumerologyPage = () => {
  const { user, profile, isAuthenticated, loading: authLoading } = useAuth();
  const { isDark } = useTheme();
  const [userProfile, setUserProfile] = useState(null);
  const [symbols, setSymbols] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('signals');
  const [bulkAnalysisLoading, setBulkAnalysisLoading] = useState(false);
  const [buySignalsLoading, setBuySignalsLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [topSymbolsLoading, setTopSymbolsLoading] = useState(false);
  const [topSymbolsCount, setTopSymbolsCount] = useState(50);

  const userId = user?.id?.toString() || null;

  useEffect(() => {
    if (isAuthenticated && profile) {
      loadUserProfileData();
    }
  }, [isAuthenticated, profile]);

  const loadUserProfileData = () => {
    if (profile && profile.profile) {
      const profileData = profile.profile;
      setUserProfile({
        life_path_number: profileData.life_path_number,
        destiny_number: profileData.destiny_number,
        soul_number: profileData.soul_number,
        personality_number: profileData.personality_number,
        power_number: profileData.power_number,
        personal_year: profileData.personal_year,
        personal_month: profileData.personal_month,
      });
    }
  };

  const handleGetTradingSignals = async () => {
    if (!isAuthenticated || !userId) {
      alert('Please login to access numerology analysis');
      return;
    }
    
    if (!symbols.trim()) {
      alert('Please enter symbols');
      return;
    }

    setLoading(true);
    try {
      const symbolList = symbols.split(',').map(s => s.trim()).filter(s => s);
      const response = await numerologyTradingClient.getTradingSignals({
        user_id: userId,
        symbols: symbolList
      });
      setResults({ type: 'signals', data: response.data });
    } catch (error) {
      console.error('Error getting trading signals:', error);
      alert('Error getting trading signals: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAnalysis = async () => {
    if (!userProfile) {
      alert('Please create your numerology profile first');
      return;
    }

    setBulkAnalysisLoading(true);
    setResults(null);
    setBulkProgress(null);

    try {
      await numerologyTradingClient.streamBulkAnalysis(
        userId,
        (progressData) => {
          setBulkProgress(progressData);
          if (progressData.type === 'complete') {
            setResults({
              type: 'bulk_analysis',
              data: progressData
            });
          }
        },
        50
      );
    } catch (error) {
      console.error('Bulk analysis error:', error);
      alert('Error: ' + error.message);
    } finally {
      setBulkAnalysisLoading(false);
    }
  };

  const handleBuySignalsOnly = async () => {
    if (!userProfile) {
      alert('Please create your numerology profile first');
      return;
    }

    setBuySignalsLoading(true);
    setResults(null);

    try {
      const result = await numerologyTradingClient.getBuySignalsOnly(userId, 50, 0.6);
      if (result.success) {
        setResults({
          type: 'buy_signals_only',
          data: result.data
        });
      }
    } catch (error) {
      console.error('BUY signals error:', error);
      alert('Error: ' + error.message);
    } finally {
      setBuySignalsLoading(false);
    }
  };

  const handleGetTopSymbols = async () => {
    if (!isAuthenticated || !userId) {
      alert('Please login to access numerology analysis');
      return;
    }

    setTopSymbolsLoading(true);
    setResults(null);

    try {
      // Get top symbols from NSE
      const response = await numerologyTradingClient.getTopSymbols(topSymbolsCount);
      if (response.success) {
        const symbolList = response.data.symbols.map(s => s.symbol).join(', ');
        setSymbols(symbolList);
        setResults({
          type: 'top_symbols',
          data: response.data
        });
      }
    } catch (error) {
      console.error('Error getting top symbols:', error);
      alert('Error getting top symbols: ' + error.message);
    } finally {
      setTopSymbolsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}>⏳</div>
        <p>Loading your numerology profile...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={styles.authPrompt}>
        <h2 style={styles.authTitle}>🔐 Authentication Required</h2>
        <p style={styles.authMessage}>
          Please login to access your personalized numerology trading analysis.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>🔢 AstroTrader AI</h1>
          <p style={styles.heroSubtitle}>
            Professional numerology-powered stock intelligence for modern traders
          </p>
          {user && (
            <div style={styles.profileSummary}>
              <h3 style={styles.profileTitle}>Welcome, {user.username}!</h3>
              <p style={styles.profileSubtitle}>Your personalized analysis is ready</p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* User Profile Card */}
        {userProfile && (
          <Card variant="elevated" padding="lg" className="fade-in">
            <CardHeader>
              <CardTitle>Your Numerology Profile</CardTitle>
              <CardDescription>
                Your personal numbers calculated from birth data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div style={styles.numerologyGrid}>
                <div style={styles.numerologyCard}>
                  <div style={styles.numerologyNumber}>{userProfile.life_path_number}</div>
                  <div style={styles.numerologyLabel}>Life Path</div>
                </div>
                <div style={styles.numerologyCard}>
                  <div style={styles.numerologyNumber}>{userProfile.destiny_number}</div>
                  <div style={styles.numerologyLabel}>Destiny</div>
                </div>
                <div style={styles.numerologyCard}>
                  <div style={styles.numerologyNumber}>{userProfile.soul_number}</div>
                  <div style={styles.numerologyLabel}>Soul</div>
                </div>
                <div style={styles.numerologyCard}>
                  <div style={styles.numerologyNumber}>{userProfile.power_number}</div>
                  <div style={styles.numerologyLabel}>Power</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analysis Tabs */}
        {userProfile && (
          <Card variant="elevated" padding="lg">
            <CardHeader>
              <div style={styles.tabContainer}>
                <Button
                  variant={activeTab === 'signals' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('signals')}
                >
                  Trading Signals
                </Button>
                <Button
                  variant={activeTab === 'bulk' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('bulk')}
                >
                  Bulk Analysis
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Trading Signals Tab */}
              {activeTab === 'signals' && (
                <div style={styles.tabContent}>
                  {/* Top Symbols Section */}
                  <div style={styles.topSymbolsSection}>
                    <h4 style={styles.sectionTitle}>📈 Get Top Market Symbols</h4>
                    <div style={styles.topSymbolsControls}>
                      <div style={styles.symbolCountGroup}>
                        <label style={styles.label}>Number of Symbols</label>
                        <select
                          value={topSymbolsCount}
                          onChange={(e) => setTopSymbolsCount(parseInt(e.target.value))}
                          style={styles.select}
                        >
                          <option value={50}>Top 50</option>
                          <option value={100}>Top 100</option>
                          <option value={200}>Top 200</option>
                        </select>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={handleGetTopSymbols}
                        loading={topSymbolsLoading}
                        disabled={topSymbolsLoading}
                        icon="📊"
                      >
                        Load Top {topSymbolsCount} Symbols
                      </Button>
                    </div>
                  </div>

                  {/* Custom Symbols Section */}
                  <div style={styles.customSymbolsSection}>
                    <h4 style={styles.sectionTitle}>🔍 Custom Symbol Analysis</h4>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Stock Symbols (comma-separated)</label>
                      <input
                        type="text"
                        value={symbols}
                        onChange={(e) => setSymbols(e.target.value)}
                        style={styles.input}
                        placeholder="e.g., INFY, TCS, RELIANCE"
                      />
                    </div>
                    <div style={styles.buttonGroup}>
                      <Button
                        variant="primary"
                        onClick={handleGetTradingSignals}
                        loading={loading}
                        disabled={loading}
                        icon="🔮"
                      >
                        Get Numerology Signals
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Bulk Analysis Tab */}
              {activeTab === 'bulk' && (
                <div style={styles.tabContent}>
                  <div style={styles.bulkActions}>
                    <Button
                      variant="secondary"
                      onClick={handleBulkAnalysis}
                      loading={bulkAnalysisLoading}
                      disabled={bulkAnalysisLoading}
                      icon="📊"
                    >
                      Analyze All NSE Symbols
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleBuySignalsOnly}
                      loading={buySignalsLoading}
                      disabled={buySignalsLoading}
                      icon="🎯"
                    >
                      Get BUY Signals Only
                    </Button>
                  </div>

                  {/* Progress Display */}
                  {bulkProgress && (
                    <div style={styles.progressContainer}>
                      <div style={styles.progressCard}>
                        <h4 style={styles.progressTitle}>
                          {bulkProgress.type === 'start' && '🚀 Starting Analysis'}
                          {bulkProgress.type === 'batch_progress' && '⚡ Processing...'}
                          {bulkProgress.type === 'complete' && '✅ Analysis Complete'}
                        </h4>
                        {bulkProgress.type === 'batch_progress' && (
                          <div style={styles.progressBar}>
                            <div style={styles.progressBarFill} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results && (
          <Card variant="elevated" padding="lg" className="fade-in">
            <CardHeader>
              <CardTitle>Analysis Results</CardTitle>
            </CardHeader>
            <CardContent>
              {results.type === 'signals' && (
                <div style={styles.signalsContainer}>
                  {results.data.signals.map((signal, index) => (
                    <div key={index} style={styles.signalCard}>
                      <div style={styles.signalHeader}>
                        <span style={styles.symbol}>{signal.symbol}</span>
                        <span style={{
                          ...styles.signalBadge,
                          backgroundColor: getSignalColor(signal.signal_type)
                        }}>
                          {signal.signal_type}
                        </span>
                        <span style={styles.confidence}>
                          {Math.round(signal.confidence * 100)}% confidence
                        </span>
                      </div>
                      <p style={styles.signalReason}>{signal.numerology_reason}</p>
                    </div>
                  ))}
                </div>
              )}

              {results.type === 'bulk_analysis' && (
                <div style={styles.bulkResults}>
                  <div style={styles.summaryStats}>
                    <div style={styles.statItem}>
                      <span style={styles.statNumber}>{results.data.summary.total_analyzed}</span>
                      <span style={styles.statLabel}>Total Analyzed</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={{...styles.statNumber, color: theme.colors.success[500]}}>
                        {results.data.summary.buy_count}
                      </span>
                      <span style={styles.statLabel}>BUY Signals</span>
                    </div>
                    <div style={styles.statItem}>
                      <span style={{...styles.statNumber, color: theme.colors.error[500]}}>
                        {results.data.summary.sell_count}
                      </span>
                      <span style={styles.statLabel}>SELL Signals</span>
                    </div>
                  </div>
                </div>
              )}

              {results.type === 'top_symbols' && (
                <div style={styles.topSymbolsResults}>
                  <div style={styles.symbolsHeader}>
                    <h4 style={styles.symbolsTitle}>📊 Top {topSymbolsCount} Market Symbols</h4>
                    <p style={styles.symbolsDescription}>
                      These symbols have been loaded and are ready for numerology analysis
                    </p>
                  </div>
                  <div style={styles.symbolsGrid}>
                    {results.data.symbols.slice(0, 20).map((symbol, index) => (
                      <div key={index} style={styles.symbolItem}>
                        <span style={styles.symbolName}>{symbol.symbol}</span>
                        <span style={styles.symbolName}>{symbol.name}</span>
                      </div>
                    ))}
                    {results.data.symbols.length > 20 && (
                      <div style={styles.moreSymbols}>
                        +{results.data.symbols.length - 20} more symbols loaded
                      </div>
                    )}
                  </div>
                  <div style={styles.symbolsAction}>
                    <Button
                      variant="primary"
                      onClick={handleGetTradingSignals}
                      loading={loading}
                      disabled={loading}
                      icon="🔮"
                    >
                      Analyze These Symbols
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const getSignalColor = (signalType) => {
  switch (signalType) {
    case 'BUY': return theme.colors.success[500];
    case 'SELL': return theme.colors.error[500];
    case 'HOLD': return theme.colors.warning[500];
    default: return theme.colors.gray[500];
  }
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
  },
  heroSection: {
    background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.secondary[500]} 100%)`,
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[8],
    color: 'white',
    textAlign: 'center',
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: '0 0 16px 0',
    lineHeight: theme.typography.lineHeight.tight,
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    opacity: 0.9,
    margin: '0 0 32px 0',
    lineHeight: theme.typography.lineHeight.relaxed,
  },
  profileSummary: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[6],
    backdropFilter: 'blur(10px)',
  },
  profileTitle: {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.semibold,
    margin: '0 0 8px 0',
  },
  profileSubtitle: {
    fontSize: theme.typography.fontSize.base,
    opacity: 0.9,
    margin: '0',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[6],
  },
  numerologyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing[4],
  },
  numerologyCard: {
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
    textAlign: 'center',
    border: '1px solid var(--card-border)',
    boxShadow: theme.shadows.sm,
  },
  numerologyNumber: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary[500],
    marginBottom: theme.spacing[2],
  },
  numerologyLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
  },
  tabContainer: {
    display: 'flex',
    gap: theme.spacing[2],
    borderBottom: '1px solid var(--border-primary)',
    paddingBottom: theme.spacing[4],
  },
  tabContent: {
    padding: theme.spacing[4],
  },
  inputGroup: {
    marginBottom: theme.spacing[6],
  },
  label: {
    display: 'block',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[2],
  },
  input: {
    width: '100%',
    padding: theme.spacing[3],
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.typography.fontSize.base,
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    transition: 'all 0.2s ease-in-out',
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing[3],
    flexWrap: 'wrap',
  },
  bulkActions: {
    display: 'flex',
    gap: theme.spacing[4],
    flexWrap: 'wrap',
    marginBottom: theme.spacing[6],
  },
  progressContainer: {
    marginTop: theme.spacing[4],
  },
  progressCard: {
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
    border: '1px solid var(--card-border)',
  },
  progressTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[3],
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: 'var(--border-primary)',
    borderRadius: theme.borderRadius.full,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: theme.colors.primary[500],
    borderRadius: theme.borderRadius.full,
    width: '60%',
    transition: 'width 0.3s ease',
  },
  signalsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[4],
  },
  signalCard: {
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing[4],
    border: '1px solid var(--card-border)',
    boxShadow: theme.shadows.sm,
  },
  signalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
    marginBottom: theme.spacing[3],
    flexWrap: 'wrap',
  },
  symbol: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  signalBadge: {
    padding: `${theme.spacing[1]} ${theme.spacing[3]}`,
    borderRadius: theme.borderRadius.full,
    color: 'white',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
  },
  confidence: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
  },
  signalReason: {
    color: 'var(--text-primary)',
    lineHeight: theme.typography.lineHeight.relaxed,
    margin: '0',
  },
  bulkResults: {
    marginTop: theme.spacing[4],
  },
  summaryStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: theme.spacing[4],
  },
  statItem: {
    textAlign: 'center',
    padding: theme.spacing[4],
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--card-border)',
  },
  statNumber: {
    display: 'block',
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[1],
  },
  statLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing[16],
    textAlign: 'center',
  },
  loadingSpinner: {
    fontSize: '48px',
    marginBottom: theme.spacing[4],
    animation: 'spin 1s linear infinite',
  },
  authPrompt: {
    textAlign: 'center',
    padding: theme.spacing[16],
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius['2xl'],
    boxShadow: theme.shadows.lg,
    maxWidth: '600px',
    margin: '0 auto',
  },
  authTitle: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[4],
  },
  authMessage: {
    fontSize: theme.typography.fontSize.lg,
    color: 'var(--text-secondary)',
    lineHeight: theme.typography.lineHeight.relaxed,
  },
  topSymbolsSection: {
    marginBottom: theme.spacing[8],
    padding: theme.spacing[4],
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    margin: '0 0 16px 0',
    textRendering: 'optimizeLegibility',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  topSymbolsControls: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing[4],
    flexWrap: 'wrap',
  },
  symbolCountGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[2],
    minWidth: '150px',
  },
  select: {
    padding: theme.spacing[3],
    border: '2px solid var(--border-primary)',
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.typography.fontSize.base,
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontWeight: theme.typography.fontWeight.medium,
    transition: 'all 0.2s ease-in-out',
    '&:focus': {
      outline: 'none',
      borderColor: 'var(--primary-500)',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  customSymbolsSection: {
    padding: theme.spacing[4],
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
  },
  topSymbolsResults: {
    marginTop: theme.spacing[4],
  },
  symbolsHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing[6],
  },
  symbolsTitle: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
  },
  symbolsDescription: {
    fontSize: theme.typography.fontSize.base,
    color: 'var(--text-secondary)',
    margin: '0',
  },
  symbolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: theme.spacing[3],
    marginBottom: theme.spacing[6],
    maxHeight: '400px',
    overflowY: 'auto',
    padding: theme.spacing[2],
    backgroundColor: 'var(--bg-primary)',
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
  },
  symbolItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing[3],
    backgroundColor: 'var(--card-bg)',
    borderRadius: theme.borderRadius.md,
    border: '1px solid var(--card-border)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'var(--bg-hover)',
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows.sm,
    },
  },
  symbolName: {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: 'var(--text-primary)',
    marginBottom: theme.spacing[1],
    '&:first-child': {
      color: theme.colors.primary[500],
      fontSize: theme.typography.fontSize.base,
    },
  },
  moreSymbols: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing[4],
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: theme.borderRadius.md,
    border: '2px dashed var(--border-primary)',
    color: 'var(--text-secondary)',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    fontStyle: 'italic',
  },
  symbolsAction: {
    display: 'flex',
    justifyContent: 'center',
  },
};

export default ProfessionalNumerologyPage;
